import type { Nullable } from '@zen/utils/typescript';

const CELL_HIGHLIGHTED_CLASS = '!bg-grey-lightest';
const MERGE_COLUMN_CELL_CLASS = 'merge-column-cell';
const TABLE_SELECTOR = 'table[data-component="table"]';
const TABLE_ROW_CLASS = 'zen-table-row';

const getCellRowSpan = (element: Nullable<HTMLTableCellElement>): number => {
  const rowSpanAttribute = element?.getAttribute('rowspan');

  return rowSpanAttribute ? parseInt(rowSpanAttribute, 10) : 1;
};

const isMergedCell = (element: Nullable<HTMLTableCellElement>): boolean => {
  return element?.classList.contains(MERGE_COLUMN_CELL_CLASS) ?? false;
};

const getOwnRows = (table: Element): HTMLTableRowElement[] => {
  const tableElement: Nullable<HTMLTableElement> = table.querySelector<HTMLTableElement>(TABLE_SELECTOR);

  if (!tableElement) {
    return [];
  }

  return Array.from(tableElement.tBodies).flatMap((body: HTMLTableSectionElement) =>
    Array.from(body.rows).filter((row: HTMLTableRowElement) => row.classList.contains(TABLE_ROW_CLASS))
  );
};

const getOwnCells = (row: HTMLTableRowElement): HTMLTableCellElement[] => Array.from(row.cells);

/**
 * Highlights cells in the specified row range.
 * Regular cells are always highlighted.
 * Merged cells are only highlighted when includeMergedCells is true
 * and their span doesn't extend beyond endRow.
 */
const highlightCellsInRows = (table: Element, startRow: number, endRow: number, includeMergedCells: boolean): void => {
  const rows: HTMLTableRowElement[] = getOwnRows(table);

  for (let index = startRow; index <= endRow; index++) {
    const row = rows[index];

    if (!row) {
      continue;
    }

    getOwnCells(row).forEach((cell: HTMLTableCellElement) => {
      const isMerged = cell.classList.contains(MERGE_COLUMN_CELL_CLASS);

      if (isMerged && (!includeMergedCells || index + getCellRowSpan(cell) - 1 > endRow)) {
        return;
      }

      cell.classList.add(CELL_HIGHLIGHTED_CLASS);
    });
  }
};

const removeAllHighlights = (tableId: string): void => {
  const table: Nullable<Element> = document.querySelector(`div.${tableId}`);

  if (!table) {
    return;
  }

  getOwnRows(table).forEach((row: HTMLTableRowElement) => {
    getOwnCells(row).forEach((cell: HTMLTableCellElement) => cell.classList.remove(CELL_HIGHLIGHTED_CLASS));
  });
};

export interface CellHoverProps {
  element: Nullable<HTMLTableCellElement>;
  rowIndex: number;
  tableId: string;
}

/**
 * Highlights cells when hovering:
 * - Regular cell: highlights only regular cells in that row
 * - Merged cell: highlights regular cells + fitting merged cells across its row span
 */
export const onCellMouseEnter = ({ element, rowIndex, tableId }: CellHoverProps): void => {
  const table: Nullable<Element> = document.querySelector(`div.${tableId}`);

  if (!table) {
    return;
  }

  const endRow = rowIndex + getCellRowSpan(element) - 1;

  highlightCellsInRows(table, rowIndex, endRow, isMergedCell(element));
};

export const onCellMouseLeave = ({ tableId }: Pick<CellHoverProps, 'tableId'>): void => {
  removeAllHighlights(tableId);
};
